import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, AlertCircle, Clock } from 'lucide-react';
import { api } from '../../utils/api';
import { toast } from 'sonner';
import Header from '../shared/Header';
import Footer from '../shared/Footer';
import SEO from '../shared/SEO';

const PaymentPage = () => {
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get('booking');
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (bookingId) {
      api.getBooking(bookingId)
        .then(res => {
          setBooking(res.data);
          if (res.data.booking_status === 'awaiting_verification') {
            setSubmitted(true);
          }
        })
        .catch(() => {
          toast.error('Failed to load booking');
          navigate('/dashboard');
        });
    } else {
      navigate('/dashboard');
    }
  }, [bookingId, navigate]);

  const handlePaymentConfirmation = async () => {
    setLoading(true);
    try {
      await api.createPayment({ booking_id: bookingId });
      setSubmitted(true);
      toast.success('Payment confirmation submitted! Admin will verify shortly.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit payment confirmation');
    } finally {
      setLoading(false);
    }
  };

  if (!booking) {
    return (
      <div className="min-h-screen bg-[#FCFBF9] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#3E2723] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FCFBF9]">
      <SEO title="Payment" description="Complete your payment at Crowns by Preshy." path="/payment" noindex />
      <Header />

      <section className="py-24">
        <div className="max-w-2xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-10">
            <h1 className="font-serif text-4xl text-[#3E2723] mb-2 tracking-tight" data-testid="payment-page-title">
              {submitted ? 'Payment Submitted' : 'Complete Your Payment'}
            </h1>
            <p className="text-[#786E66]">
              Booking Reference: <strong data-testid="payment-booking-ref">{booking.booking_reference}</strong>
            </p>
          </div>

          {/* Booking Summary */}
          <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-[#E6DCD3] p-8 mb-6" data-testid="payment-booking-summary">
            <h3 className="font-serif text-lg text-[#3E2723] mb-5">Booking Summary</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between py-2 border-b border-[#E6DCD3]">
                <span className="text-[#786E66]">Service</span>
                <span className="text-[#1A1A1A] font-medium" data-testid="payment-hairstyle-name">{booking.hairstyle_name}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-[#E6DCD3]">
                <span className="text-[#786E66]">Category</span>
                <span className="text-[#1A1A1A]">{booking.category}</span>
              </div>
              {booking.selected_variant && (
                <div className="flex justify-between py-2 border-b border-[#E6DCD3]">
                  <span className="text-[#786E66]">Variant</span>
                  <span className="text-[#1A1A1A]">{booking.selected_variant}</span>
                </div>
              )}
              {booking.add_ons && booking.add_ons.length > 0 && (
                <div className="flex justify-between py-2 border-b border-[#E6DCD3]">
                  <span className="text-[#786E66]">Add-ons</span>
                  <span className="text-[#1A1A1A]">{booking.add_ons.map(a => a.name).join(', ')}</span>
                </div>
              )}
              <div className="flex justify-between py-2 border-b border-[#E6DCD3]">
                <span className="text-[#786E66]">Appointment</span>
                <span className="text-[#1A1A1A]">{booking.appointment_date} at {booking.appointment_time}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-[#E6DCD3]">
                <span className="text-[#786E66]">Duration</span>
                <span className="text-[#1A1A1A]">{booking.duration_minutes} mins</span>
              </div>
              <div className="flex justify-between py-3 mt-2">
                <span className="text-[#1A1A1A] font-semibold text-base">Total Amount</span>
                <span className="text-[#3E2723] font-bold text-xl" data-testid="payment-total-amount">
                  ₦{booking.total_amount?.toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          {submitted ? (
            /* Awaiting Verification State */
            <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-[#E6DCD3] p-8 text-center" data-testid="payment-awaiting-state">
              <div className="h-16 w-16 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-5">
                <Clock className="h-8 w-8 text-amber-600" />
              </div>
              <h3 className="font-serif text-xl text-[#3E2723] mb-3">Awaiting Verification</h3>
              <p className="text-[#786E66] text-sm mb-6">
                Your payment confirmation has been submitted. The admin will check the bank account and verify your payment. You'll receive an email once it's confirmed.
              </p>
              <button
                onClick={() => navigate('/dashboard')}
                className="bg-[#3E2723] text-white hover:bg-[#2A1A17] rounded-full px-8 py-3 font-medium transition-all hover:-translate-y-0.5"
                data-testid="go-to-dashboard-btn"
              >
                Go to Dashboard
              </button>
            </div>
          ) : (
            /* Payment Instructions & Action */
            <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-[#E6DCD3] p-8" data-testid="payment-action-area">
              <h3 className="font-serif text-lg text-[#3E2723] mb-5">Payment Instructions</h3>

              <div className="bg-[#F3EBE1] rounded-xl p-6 mb-6" data-testid="payment-instructions">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-[#3E2723] flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-[#786E66] space-y-3">
                    <p><strong className="text-[#3E2723]">How to Pay:</strong></p>
                    <p>1. Transfer <strong className="text-[#1A1A1A]">₦{booking.total_amount?.toLocaleString()}</strong> to:</p>
                    <div className="bg-white rounded-lg p-4 border border-[#E6DCD3] space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-[#786E66]">Account Name:</span>
                        <span className="text-[#1A1A1A] font-medium" data-testid="bank-account-name">Igboekwu Chinaza Precious</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-[#786E66]">Bank:</span>
                        <span className="text-[#1A1A1A] font-medium" data-testid="bank-name">UBA</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-[#786E66]">Account Number:</span>
                        <span className="flex items-center gap-2">
                          <span className="text-[#1A1A1A] font-semibold tracking-wide" data-testid="bank-account-number">2239756789</span>
                          <button
                            type="button"
                            onClick={() => { navigator.clipboard.writeText('2239756789'); toast.success('Account number copied!'); }}
                            className="text-xs text-[#3E2723] bg-[#F3EBE1] border border-[#E6DCD3] hover:bg-[#E6DCD3] rounded px-2 py-0.5 transition-colors"
                            data-testid="copy-account-btn"
                          >
                            Copy
                          </button>
                        </span>
                      </div>
                    </div>
                    <p>2. Use your Booking Reference <strong className="text-[#1A1A1A]">{booking.booking_reference}</strong> as the transfer description/narration if possible.</p>
                    <p>3. After completing the transfer, click <strong className="text-[#1A1A1A]">I've Made the Payment</strong>.</p>
                    <p>4. Admin will check the bank account and confirm your payment.</p>
                  </div>
                </div>
              </div>

              <button
                onClick={handlePaymentConfirmation}
                disabled={loading}
                className="w-full bg-[#3E2723] text-white hover:bg-[#2A1A17] rounded-full py-4 font-medium text-base transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="payment-confirm-btn"
              >
                {loading ? (
                  'Submitting...'
                ) : (
                  <>
                    <CheckCircle className="h-5 w-5" />
                    I've Made the Payment
                  </>
                )}
              </button>

              <p className="text-xs text-[#786E66] text-center mt-4">
                Only click this button after you have completed your bank transfer.
              </p>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PaymentPage;
